// src/pages/Enseignants/ListingAdherents/ModalFicheAdherent.jsx
import React, { useMemo, useState } from "react";
import {
  FaTimes,
  FaExternalLinkAlt,
  FaEdit,
  FaPlus,
  FaCalendar,
} from "react-icons/fa";
import { Link } from "react-router-dom";

// Reuse admin components
import AdherentDetails from "../../PagesAdministrateurs/DetailsAdherent/AdherentDetails";
import AdherentGroupCategory from "../../PagesAdministrateurs/DetailsAdherent/AdherentGroupCategory";
import AdherentManagers from "../../PagesAdministrateurs/DetailsAdherent/AdherentManagers";
import AdherentPassageCeinture from "../../PagesAdministrateurs/DetailsAdherent/AdherentPassageCeinture";
import AdherentPalmares from "../../PagesAdministrateurs/DetailsAdherent/AdherentPalmares";
import AdherentCompetitions from "../../PagesAdministrateurs/DetailsAdherent/AdherentCompetitions";
import DossierInscription from "../../PagesAdministrateurs/DetailsAdherent/DossierInscription";
import ReglementInscription from "../../PagesAdministrateurs/DetailsAdherent/ReglementInscription";
import AchatsAdherent from "../../PagesAdministrateurs/DetailsAdherent/AchatsAdherent";
import AttendanceList from "../../PagesAdministrateurs/DetailsAdherent/AttendanceList";

// 👉 importe la modale d’édition (chemin demandé)
import EditAdherentAdminModal from "./../../PagesAdministrateurs/ListingAdherentsAdmin/EditAdherentAdminModal";

// Avatar par défaut
const defaultAvatar = (genre) =>
  genre === "Féminin" ? "/img/judokate_avatar.png" : "/img/judoka_avatar.png";

// Badge statut
const getStatusPillClass = (statut) => {
  switch (statut) {
    case "Licencié":
      return "bg-green-300 text-slate-800 ring-1 ring-green-300/70";
    case "Licencié Hors Club":
      return "bg-green-200 text-slate-800 ring-1 ring-green-200/70";
    case "Inscrit":
      return "bg-yellow-300 text-slate-900 ring-1 ring-yellow-300/70";
    case "Cours d'essai":
      return "bg-red-300 text-slate-900 ring-1 ring-red-300/70";
    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
      return "bg-slate-300 text-slate-800 ring-1 ring-slate-300/70";
    case "Ancien Licencié":
      return "bg-slate-900 text-white ring-1 ring-slate-900/50";
    case "Non Licencié - Arrêt":
      return "bg-slate-500 text-white ring-1 ring-slate-500/70";
    default:
      return "bg-slate-200 text-slate-800 ring-1 ring-slate-200/70";
  }
};

const TABS = [
  { key: "profil", label: "Profil" },
  { key: "groupes", label: "Groupes" },
  { key: "managers", label: "Managers" },
  { key: "ceintures", label: "Ceintures" },
  { key: "competitions", label: "Compétitions" },
  { key: "palmares", label: "Palmarès" },
  { key: "dossier", label: "Dossier" },
  { key: "reglement", label: "Règlement" },
  { key: "achats", label: "Achats" },
  { key: "presences", label: "Présences" },
];

const ModalFicheAdherent = ({
  adherent, // objet complet (depuis openModal(a))
  onClose, // closeModal()
  isAdmin, // bool
  onEdit, // optionnel : () => openEditModal(adherent) (parent)
  onManageGroups, // optionnel : () => openManageGroupModal(adherent)
}) => {
  const [tab, setTab] = useState("profil");
  const id = adherent?.id;

  // Fallback local : ouvrir la modale d’édition ici si onEdit n’est pas fourni
  const [isEditOpen, setIsEditOpen] = useState(false);

  const headerName = useMemo(
    () =>
      `${(adherent?.nom || "").toUpperCase()} ${adherent?.prenom || ""}`.trim(),
    [adherent]
  );

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    if (
      /^https?:\/\//i.test(s) ||
      s.startsWith("data:") ||
      s.startsWith("blob:")
    ) {
      return s;
    }
    // assets front → on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  if (!adherent) return null;

  // handler d’édition : préfère la prop parent, sinon fallback local
  const handleEdit = () => {
    if (onEdit) return onEdit(adherent);
    setIsEditOpen(true);
  };

  return (
    <div className="fixed inset-0 z-[999]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div
        className="relative mx-auto my-2 sm:my-6 w-[100vw] sm:w-[92vw] max-w-[1100px]
                   h-[calc(100vh-1rem)] sm:h-[85vh]
                   rounded-none sm:rounded-2xl overflow-hidden
                   bg-white dark:bg-slate-900 shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header gradient */}
        <div className="relative bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white">
          <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3">
            <img
              src={
                adherent?.photo_url
                  ? toAbsolute(adherent.photo_url)
                  : defaultAvatar(adherent?.sexe)
              }
              alt={headerName}
              className="h-12 w-12 sm:h-14 sm:w-14 rounded-full object-cover ring-2 ring-white/70"
            />
            <div className="min-w-0">
              <div className="text-base sm:text-lg font-semibold truncate">
                {headerName}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 ${getStatusPillClass(
                    adherent?.statut_inscription
                  )}`}
                >
                  {adherent?.statut_inscription || "—"}
                </span>
                {adherent?.saison_active && (
                  <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-emerald-200 text-emerald-900 ring-1 ring-emerald-300">
                    <FaCalendar /> {adherent.saison_active}
                  </span>
                )}
              </div>
            </div>

            {/* Actions droite */}
            <div className="ml-auto flex items-center gap-2">
              {isAdmin && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost text-white hover:bg-white/15 gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit();
                    }}
                    title="Éditer l’adhérent"
                  >
                    <FaEdit /> <span className="hidden sm:inline">Éditer</span>
                  </button>

                  <button
                    type="button"
                    className="btn btn-sm btn-ghost text-white hover:bg-white/15 gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      onManageGroups?.(adherent);
                    }}
                    title="Gérer les groupes"
                  >
                    <FaPlus /> <span className="hidden sm:inline">Groupes</span>
                  </button>
                </>
              )}

              {id && (
                <Link
                  to={`/users/fiche-adherent/${id}`}
                  className="btn btn-sm btn-ghost text-white hover:bg-white/15 gap-2"
                  title="Ouvrir en plein écran"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FaExternalLinkAlt />
                  <span className="hidden sm:inline">Plein écran</span>
                </Link>
              )}

              <button
                type="button"
                onClick={onClose}
                className="btn btn-sm btn-ghost text-white hover:bg-white/15"
                aria-label="Fermer"
                title="Fermer"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Onglets (scrollables) */}
          <div className="px-2 sm:px-4 pb-2">
            <div className="no-scrollbar overflow-x-auto">
              <div className="inline-flex gap-2">
                {TABS.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setTab(key)}
                    className={`px-3 py-1.5 rounded-full text-sm transition
                                ${
                                  tab === key
                                    ? "bg-white text-slate-900"
                                    : "bg:white/10 bg-white/10 text-white hover:bg-white/20"
                                }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Contenu scrollable */}
        <div className="h-[calc(100%-128px)] sm:h-[calc(85vh-120px)] overflow-y-auto px-4 sm:px-6 py-4">
          {tab === "profil" && (
            <AdherentDetails
              adherent={adherent}
              showTeacherNotes={false}
              // 👉 passe onEdit jusqu’au bouton d’édition dans AdherentDetails
              onEdit={(a) => {
                // a = adherent retourné par le composant enfant ; fallback local
                if (onEdit) return onEdit(a);
                setIsEditOpen(true);
              }}
            />
          )}
          {tab === "groupes" && <AdherentGroupCategory adherentId={id} />}
          {tab === "managers" && <AdherentManagers adherentId={id} />}
          {tab === "ceintures" && <AdherentPassageCeinture adherentId={id} />}
          {tab === "competitions" && <AdherentCompetitions adherentId={id} />}
          {tab === "palmares" && <AdherentPalmares adherentId={id} />}
          {tab === "dossier" && <DossierInscription adherentId={id} />}
          {tab === "reglement" && (
            <ReglementInscription
              adherentId={id}
              saison={adherent?.saison_active}
            />
          )}
          {tab === "achats" && <AchatsAdherent adherentId={id} />}
          {tab === "presences" && <AttendanceList adherentId={id} />}
        </div>
      </div>

      {/* Fallback : modale d’édition locale si aucun onEdit n’est fourni */}
      {isAdmin && isEditOpen && adherent && !onEdit && (
        <EditAdherentAdminModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          refreshList={null} // optionnel, la modale gère déjà le ?.()
          adherent={adherent}
        />
      )}
    </div>
  );
};

export default ModalFicheAdherent;
